import { isWeb, ResponsiveImage, YStack } from "@medbillai/ui";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { useNotificationPermission } from "../../lib/push-notifications";
import type { Intent, ResolvedIntentData } from "./types";
export type NotificationsOutcomes = {
  configured: {
    enabled: boolean;
  };
  skipped: {};
};
const Outcome = tagged<NotificationsOutcomes>();
type Variant = Intent | "generic";

// Figma (1793:27784): the notification mock renders at 300px wide, centered;
// the 1140px-wide PNGs are high-res exports, never shown near natural size.
const MOCK_IMAGE_MAX_WIDTH = 300;
const INTENT_COPY: Record<Variant, {
  title: string;
  subtitle: string;
  altText: string;
  image: {
    web: string;
    native: string;
  };
}> = {
  bill: {
    title: "Never miss out on your savings",
    subtitle: "We'll notify you when we find an overcharge, win a dispute, or a deadline is coming up.",
    altText: "We found you potential savings on your latest case.",
    image: {
      web: "/static/onboarding/notifications-intent-bill.png",
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
      native: require("../../assets/onboarding/notifications-intent-bill.png")
    }
  },
  save: {
    title: "Never miss out on your savings",
    subtitle: "We'll flag important deadlines and anything on your plan that could cost you money.",
    altText: "Physical therapy is now covered as part of your Aetna plan.",
    image: {
      web: "/static/onboarding/notifications-intent-learn.png",
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
      native: require("../../assets/onboarding/notifications-intent-learn.png")
    }
  },
  generic: {
    title: "Your advocate is always on",
    subtitle: "Turn on notifications so we can reach you when something needs your attention.",
    altText: "We have an update for you on your account.",
    image: {
      web: "/static/onboarding/notifications-intent-other.png",
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-require-imports
      native: require("../../assets/onboarding/notifications-intent-other.png")
    }
  }
};
export function Notifications({
  intent,
  onDone
}: ResolvedIntentData & {
  onDone: OnDone<NotificationsOutcomes>;
}) {
  const {
    allowPermissions
  } = useNotificationPermission();
  const variant: Variant = intent ?? "generic";
  const copy = INTENT_COPY[variant];
  const handleAllow = async () => {
    // Advancing on `false` is deliberate: the hook already alerts on real
    // failures, and OS-level denial is a valid choice - `enabled` records the
    // result for analytics either way.
    const enabled = await allowPermissions();
    onDone(Outcome.configured({
      enabled
    }));
  };
  return <OnboardingScreen data-sentry-element="OnboardingScreen" data-sentry-component="Notifications" data-sentry-source-file="Notifications.tsx">
      <OnboardingScreen.Nav showLogo={false} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Notifications.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Notifications.tsx">
        <OnboardingScreen.Header alignment="center" title={copy.title} subtitle={copy.subtitle} data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Notifications.tsx" />
        <YStack flex={1} alignItems="center" justifyContent="center" maxWidth="100%" width="auto" data-sentry-element="YStack" data-sentry-source-file="Notifications.tsx">
          <ResponsiveImage testID={`notif-permission.variant.${variant}`} src={isWeb ? copy.image.web : copy.image.native} alt={copy.altText} objectFit="contain" maxWidth={MOCK_IMAGE_MAX_WIDTH} data-sentry-element="ResponsiveImage" data-sentry-source-file="Notifications.tsx" />
        </YStack>
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Notifications.tsx">
        <OnboardingScreen.PrimaryAction testID="notif-permission.allow" variant="brand" onPress={handleAllow} data-sentry-element="OnboardingScreen.PrimaryAction" data-sentry-source-file="Notifications.tsx">
          Turn on notifications
        </OnboardingScreen.PrimaryAction>
        <OnboardingScreen.TertiaryAction testID="notif-permission.skip" onPress={() => onDone(Outcome.skipped())} data-sentry-element="OnboardingScreen.TertiaryAction" data-sentry-source-file="Notifications.tsx">
          Skip
        </OnboardingScreen.TertiaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}